import React from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Rule, RuleSeverity } from 'types/dt-graphql-apollo-v2';
import { sortStringOrUndefined } from 'utils/misc';
import { SeverityTag, TableContainer, LabelListDisplay } from 'components';
import { PAGINATION_PAGE_SIZE } from 'utils/config';
import { Body as Label } from '@cognite/cogs.js';
import { RuleOptions } from './RuleOptions';

export const RuleList = ({
  rules,
  onDeleteRuleComplete,
  onEditRule,
  showPagination = false,
}: {
  rules?: Rule[] | [] | null;
  onDeleteRuleComplete: VoidFunction;
  onEditRule: (rule: Rule) => void;
  showPagination: boolean;
}) => {
  const renderRuleOptionsMenu = (rule: Rule) => (
    <RuleOptions
      rule={rule}
      onDeleteRuleComplete={onDeleteRuleComplete}
      onEditRule={onEditRule}
    />
  );

  const columns: ColumnsType<Rule> = [
    {
      title: 'Rule number',
      key: 'customerDefinedId',
      render: (record: Rule) => record?.customerDefinedId,
      sorter: (a, b) =>
        sortStringOrUndefined(a?.customerDefinedId, b?.customerDefinedId),
    },
    {
      title: 'Name',
      key: 'name',
      render: (record: Rule) => record.name,
      sorter: (a, b) => sortStringOrUndefined(a?.name, b?.name),
    },
    {
      title: 'Severity',
      key: 'severity',
      render: (record: Rule) =>
        record?.severity && (
          <SeverityTag severity={record?.severity as RuleSeverity} />
        ),
      sorter: (a, b) => sortStringOrUndefined(a?.severity, b?.severity),
    },
    {
      title: 'Disabled',
      key: 'disabled',
      sorter: (a, b) =>
        (a?.tags?.indexOf('_disabled') || 0) -
        (b?.tags?.indexOf('_disabled') || 0),
      render: (record: Rule) => (
        <>{record.tags?.includes('_disabled') && <Label>Disabled</Label>}</>
      ),
    },
    {
      title: 'Labels',
      key: 'tags',
      render: (record: Rule) => (
        <LabelListDisplay
          labels={record.tags
            ?.map((tag) => ({ text: tag, key: tag }))
            .filter((tag) => tag.text !== '_disabled')}
        />
      ),
      sorter: (a, b) => {
        const sortedATags = a?.tags?.slice().sort(sortStringOrUndefined);
        const sortedBTags = b?.tags?.slice().sort(sortStringOrUndefined);
        return sortStringOrUndefined(sortedATags?.[0], sortedBTags?.[0]);
      },
    },
    {
      key: 'options',
      render: renderRuleOptionsMenu,
    },
  ];

  return (
    <TableContainer>
      <Table<Rule>
        rowKey="id"
        dataSource={rules || []}
        columns={columns}
        pagination={
          showPagination
            ? { defaultPageSize: PAGINATION_PAGE_SIZE, hideOnSinglePage: true }
            : false
        }
      />
    </TableContainer>
  );
};
